import { Button } from 'blaze-ui/Button'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCallback, useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { IntegrationsDrawer } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/IntegrationsDrawer'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useConnectAccount } from 'hooks/useConnectAccount'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import X02 from 'blaze-ui/icons/16/X02'
import { IntegrationButton } from './IntegrationButton/IntegrationButton'
import { ProgressLoader } from './ProgressLoader/ProgressLoader'
import { getProviderForTactic } from './integrationConfig'
import { useMissingRequiredProviders } from './useMissingRequiredProviders'
import Styles from './IntegrationsBanner.module.scss'

const DISMISSAL_KEY = 'integrations-banner-dismissed'

export const IntegrationsBanner = () => {
  const isIntegrationsBannerEnabled = useFlipperFlag('integrations_banner')
  const { data: brandPlan } = useCurrentBrandPlan()
  const { openModal } = useModals()
  const { isMobile } = useResponsiveDesign()
  const rootStore = useRootStore()
  const {
    drawersStore: { openDrawer },
  } = rootStore
  const connectAccount = useConnectAccount()
  const workspaceId = useCurrentWorkspaceId()
  const [localDismissed, setLocalDismissed] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const dismissalKey = `${DISMISSAL_KEY}-${workspaceId}`
  const dismissed = localDismissed || !!localStorage.getItem(dismissalKey)

  const { integrationsFromSchedules, missingIntegrations, connectedCount, totalCount, nextIntegration } =
    useMissingRequiredProviders()

  const openIntegrationModal = () => {
    if (isMobile) {
      openDrawer(IntegrationsDrawer)
    } else {
      openModal(IntegrationsModal, {})
    }
  }

  const progressPercentage = totalCount > 0 ? (connectedCount / totalCount) * 100 : 0

  const handleConnectIntegration = useCallback(
    (integration: string) => {
      const provider = getProviderForTactic(integration, brandPlan?.selected_blog_platform)
      if (provider) {
        connectAccount(provider)
      }
    },
    [connectAccount, brandPlan?.selected_blog_platform],
  )

  const handleDismiss = () => {
    setIsClosing(true)
    setTimeout(() => {
      localStorage.setItem(dismissalKey, 'true')
      setLocalDismissed(true)
    }, 300)
  }

  if (
    !isIntegrationsBannerEnabled ||
    dismissed ||
    !brandPlan ||
    integrationsFromSchedules.length === 0 ||
    missingIntegrations.length === 0 ||
    !nextIntegration ||
    isMobile
  ) {
    return null
  }

  return (
    <div className={`${Styles.banner} ${isClosing ? Styles.closing : ''}`}>
      <div className={Styles.progressSection}>
        <div className={Styles.circularProgress}>
          <ProgressLoader percentage={progressPercentage} size={28} />
        </div>
        <div className={Styles.progressText}>
          {connectedCount}/{totalCount} accounts connected!
        </div>
      </div>
      <div className={Styles.content}>
        <div className={Styles.nextUpSection}>
          <span className={Styles.nextUpText}>Next up</span>
          <IntegrationButton
            integration={nextIntegration}
            selectedBlogPlatform={brandPlan?.selected_blog_platform}
            onPress={() => handleConnectIntegration(nextIntegration)}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button variant="tertiary" size="sm" onPress={openIntegrationModal} className={Styles.seeAllButton}>
          See All Integrations →
        </Button>
        <button onClick={handleDismiss} className={Styles.closeButton} aria-label="Dismiss banner">
          <X02 size={20} color="var(--dark-80)" />
        </button>
      </div>
    </div>
  )
}
